import React from 'react';
import Helmet from 'react-helmet';
import { Route, Routes } from 'react-router-dom';
import { Container, Stack, useMediaQuery, useTheme } from '@mui/material';
import { useQuery } from '@apollo/client';
import { useIntl } from 'react-intl';

import { Breadcrumbs, BurgerMenu, Header, Main } from '../../components';
import {
  GET_MY_OBJECTS_QUERY,
  GET_MY_OBJECT_GROUPS_QUERY,
  GET_MY_SITES_QUERY,
} from '../../gql';
import { ObjectsMenu } from './ObjectsMenu';
import { ObjectsTable } from './ObjectsTable';
import { ObjectGroupsTable } from './ObjectGroupsTable';
import { ObjectGroupDetail } from './ObjectGroupDetail';

const contentStyles = { flexGrow: 1, gap: 1, minWidth: 0, pb: 3 };

const ObjectsIndex = () => {
  const { data, loading } = useQuery(GET_MY_OBJECTS_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const {
    data: sitesData,
    loading: sitesLoading,
  } = useQuery(GET_MY_SITES_QUERY, { fetchPolicy: 'cache-first' });
  const {
    data: groupsData,
    loading: groupsLoading,
  } = useQuery(GET_MY_OBJECT_GROUPS_QUERY, { fetchPolicy: 'cache-first' });
  const rows = data?.getMyObjects ?? [];
  const sites = sitesData?.getMySites ?? [];
  const groups = groupsData?.getMyObjectGroups ?? [];
  return (
    <Main>
      <ObjectsTable
        groups={groups}
        rows={rows}
        sites={sites}
        loading={loading || sitesLoading || groupsLoading}
      />
    </Main>
  );
};

const ObjectGroupsIndex = () => {
  const { data, loading } = useQuery(GET_MY_OBJECT_GROUPS_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const rows = data?.getMyObjectGroups ?? [];
  return (
    <Main>
      <ObjectGroupsTable rows={rows} loading={loading} />
    </Main>
  );
};

const Objects = React.memo(() => {
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <Helmet
        title={intl.formatMessage({
          id: 'objects.pageTitle',
          defaultMessage: 'Objects | Acolyte',
        })}
      />
      <Header mainIcon={isMobile && <BurgerMenu menu={<ObjectsMenu />} />} />
      <Container maxWidth="xl">
        <Stack direction="row">
          {!isMobile && <ObjectsMenu />}
          <Stack sx={contentStyles}>
            <Stack
              direction={isMobile ? 'column' : 'row'}
              justifyContent="space-between"
              alignItems={isMobile ? 'flex-start' : 'center'}
            >
              <Breadcrumbs />
            </Stack>
            <Routes>
              <Route index element={<ObjectsIndex />} />
              <Route path="groups" element={<ObjectGroupsIndex />} />
              <Route
                path="groups/:groupId"
                element={<ObjectGroupDetail activeTab="overview" />}
              />
              <Route
                path="groups/:groupId/objects"
                element={<ObjectGroupDetail activeTab="objects" />}
              />
            </Routes>
          </Stack>
        </Stack>
      </Container>
    </div>
  );
});

export { Objects };
